import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import NavBar from '../components/NavBar';
import apiClient from '../api/client';
import { CATEGORIES, CATEGORY_COLORS, formatDate, todayString, TITLE_MAX, CONTENT_MAX, TAGS_MAX, TAG_LENGTH_MAX } from '../utils/helpers';
import useWindowWidth from '../hooks/useWindowWidth';
import useSpeechRecognition from '../hooks/useSpeechRecognition';
import ConfirmDialog from '../components/ConfirmDialog';

// T23: edit-mode autosave to localStorage. Keyed per entry so concurrent
// edits on different entries don't collide.
const EDIT_DRAFT_KEY_PREFIX = 'bragsheet_draft_edit_';
const AUTOSAVE_DEBOUNCE_MS = 1500;
const DRAFT_MAX_AGE_MS = 24 * 60 * 60 * 1000;

function formatSavedTime(ts) {
  try {
    const d = new Date(ts);
    return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', second: '2-digit' });
  } catch {
    return '';
  }
}

function formatDraftTimestamp(ts) {
  try {
    const d = new Date(ts);
    return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  } catch {
    return '';
  }
}

const styles = {
  page: { minHeight: '100vh', backgroundColor: '#f8fafc' },
  pageContent: { maxWidth: '760px', margin: '0 auto', padding: '2rem 1.5rem' },
  backLink: { color: '#01696F', textDecoration: 'none', fontSize: '0.875rem', fontWeight: '500', display: 'inline-block', marginBottom: '1.5rem' },
  card: {
    backgroundColor: '#ffffff',
    border: '1px solid #e2e8f0',
    borderRadius: '12px',
    padding: '2rem',
  },
  titleRow: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '1rem', gap: '1rem' },
  entryTitle: { fontSize: '1.5rem', fontWeight: '700', color: '#1e293b', lineHeight: '1.3' },
  meta: { display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '1.5rem' },
  badge: (color) => ({
    display: 'inline-block',
    padding: '0.25rem 0.7rem',
    borderRadius: '999px',
    backgroundColor: color + '1a',
    color: color,
    fontSize: '0.8rem',
    fontWeight: '600',
  }),
  date: { fontSize: '0.875rem', color: '#94a3b8' },
  entryText: { fontSize: '1rem', color: '#1e293b', lineHeight: '1.8', whiteSpace: 'pre-wrap', marginBottom: '1.5rem' },
  tags: { display: 'flex', gap: '0.4rem', flexWrap: 'wrap', marginBottom: '1.5rem' },
  tag: {
    padding: '0.2rem 0.6rem',
    borderRadius: '4px',
    backgroundColor: '#f1f5f9',
    color: '#64748b',
    fontSize: '0.8rem',
  },
  btnRow: { display: 'flex', gap: '0.75rem' },
  btn: {
    padding: '0.75rem 1.25rem',
    borderRadius: '8px',
    fontSize: '0.875rem',
    fontWeight: '600',
    cursor: 'pointer',
    border: 'none',
    transition: 'opacity 0.15s',
    minHeight: '44px',
  },
  primaryBtn: { backgroundColor: '#01696F', color: '#ffffff' },
  outlineBtn: { backgroundColor: '#ffffff', color: '#01696F', border: '1px solid #01696F' },
  dangerBtn: { backgroundColor: '#fef2f2', color: '#ef4444', border: '1px solid #fecaca' },
  btnDisabled: { opacity: 0.6, cursor: 'not-allowed' },
  formGroup: { marginBottom: '1.25rem' },
  label: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    fontSize: '0.875rem',
    fontWeight: '500',
    color: '#1e293b',
    marginBottom: '0.4rem',
  },
  input: {
    width: '100%',
    padding: '0.75rem 0.875rem',
    borderRadius: '8px',
    border: '1px solid #e2e8f0',
    fontSize: '0.9rem',
    color: '#1e293b',
    outline: 'none',
  },
  textarea: {
    width: '100%',
    padding: '0.75rem 0.875rem',
    borderRadius: '8px',
    border: '1px solid #e2e8f0',
    fontSize: '0.9rem',
    color: '#1e293b',
    outline: 'none',
    resize: 'vertical',
    minHeight: '200px',
    fontFamily: 'inherit',
    lineHeight: '1.6',
  },
  select: {
    width: '100%',
    padding: '0.75rem 0.875rem',
    borderRadius: '8px',
    border: '1px solid #e2e8f0',
    fontSize: '0.9rem',
    color: '#1e293b',
    backgroundColor: '#ffffff',
    outline: 'none',
  },
  chip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.35rem',
    padding: '0.25rem 0.6rem',
    backgroundColor: '#f0fdfa',
    color: '#01696F',
    border: '1px solid #99f6e4',
    borderRadius: '999px',
    fontSize: '0.8rem',
    fontWeight: '500',
  },
  chipRemove: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: '#01696F',
    fontSize: '0.9rem',
    lineHeight: '1',
    padding: '0',
  },
  tagHint: { fontSize: '0.78rem', color: '#94a3b8', marginTop: '0.3rem' },
  error: {
    color: '#ef4444',
    fontSize: '0.875rem',
    padding: '0.75rem',
    backgroundColor: '#fef2f2',
    borderRadius: '8px',
    marginBottom: '1rem',
  },
  loadingText: { textAlign: 'center', color: '#64748b', padding: '3rem' },
  // T27: prominent voice-input row — full button with icon + text label,
  // dedicated placement under the Content textarea. Bigger icon (24x24+)
  // and obvious recording affordance when active (animated red pulse).
  voiceRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    marginTop: '0.5rem',
  },
  voiceBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.6rem',
    padding: '0.55rem 1rem',
    borderRadius: '999px',
    border: '1px solid #99f6e4',
    backgroundColor: '#f0fdfa',
    color: '#01696F',
    cursor: 'pointer',
    fontSize: '0.875rem',
    fontWeight: '600',
    transition: 'background-color 0.15s, border-color 0.15s, color 0.15s',
  },
  voiceBtnHover: {
    backgroundColor: '#ccfbf1',
    borderColor: '#01696F',
  },
  voiceBtnActive: {
    backgroundColor: '#ef4444',
    borderColor: '#ef4444',
    color: '#ffffff',
    boxShadow: '0 0 0 4px rgba(239, 68, 68, 0.25)',
  },
  voiceBtnActiveHover: {
    backgroundColor: '#dc2626',
    borderColor: '#dc2626',
  },
  recordingDot: {
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    backgroundColor: '#ffffff',
    display: 'inline-block',
    animation: 'bragsheetVoicePulse 1s ease-in-out infinite',
  },
  micNotSupported: {
    fontSize: '0.8rem',
    color: '#94a3b8',
    fontStyle: 'italic',
    marginTop: '0.25rem',
  },
  speechError: {
    fontSize: '0.8rem',
    color: '#ef4444',
    marginTop: '0.25rem',
  },
  interimText: {
    fontSize: '0.8rem',
    color: '#94a3b8',
    fontStyle: 'italic',
    marginTop: '0.25rem',
    padding: '0.4rem 0.6rem',
    backgroundColor: '#f8fafc',
    borderRadius: '6px',
    border: '1px dashed #e2e8f0',
  },
};

export default function EntryDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isMobile } = useWindowWidth();
  const [entry, setEntry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  // edit state
  const [editTitle, setEditTitle] = useState('');
  const [editContent, setEditContent] = useState('');
  const [editCategory, setEditCategory] = useState('');
  const [editTags, setEditTags] = useState([]);
  const [editTagInput, setEditTagInput] = useState('');
  const [editDate, setEditDate] = useState('');
  const [tagLoading, setTagLoading] = useState(false);
  const [tagError, setTagError] = useState('');

  // T23: per-entry edit-mode autosave.
  const editDraftKey = `${EDIT_DRAFT_KEY_PREFIX}${id}`;
  const [autosaveStatus, setAutosaveStatus] = useState('idle');
  const [autosaveSavedAt, setAutosaveSavedAt] = useState(null);
  const [editDraftPrompt, setEditDraftPrompt] = useState(null);

  const { isListening, transcript, isSupported, startListening, stopListening, error: speechError } = useSpeechRecognition();
  const lastTranscriptRef = useRef('');

  useEffect(() => {
    if (transcript && transcript !== lastTranscriptRef.current) {
      lastTranscriptRef.current = transcript;
    }
  }, [transcript]);

  const handleToggleRecording = () => {
    if (isListening) {
      stopListening();
      if (lastTranscriptRef.current.trim()) {
        setEditContent(prev => {
          const separator = prev.length > 0 && !prev.endsWith(' ') && !prev.endsWith('\n') ? ' ' : '';
          return prev + separator + lastTranscriptRef.current.trim();
        });
        lastTranscriptRef.current = '';
      }
    } else {
      startListening();
    }
  };

  const fetchEntry = useCallback(() => {
    setError('');
    setLoading(true);
    apiClient.get(`/api/entries/${id}`)
      .then(res => {
        const e = res.data.data?.entry || res.data.data;
        setEntry(e);
      })
      .catch(() => setError('Entry not found.'))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => { fetchEntry(); }, [fetchEntry]);

  const startEdit = () => {
    setEditTitle(entry.title);
    setEditContent(entry.content);
    setEditCategory(entry.category);
    const t = Array.isArray(entry.tags)
      ? entry.tags
      : (typeof entry.tags === 'string' ? JSON.parse(entry.tags || '[]') : []);
    setEditTags(t);
    setEditDate(entry.entryDate ? entry.entryDate.split('T')[0] : todayString());
    setEditing(true);
    setAutosaveStatus('idle');
    setAutosaveSavedAt(null);
    // T23: look for a recent edit-mode draft for this entry.
    try {
      const raw = window.localStorage.getItem(editDraftKey);
      if (!raw) return;
      const draft = JSON.parse(raw);
      if (!draft || typeof draft !== 'object') return;
      const age = Date.now() - (draft.savedAt || 0);
      if (age < 0 || age > DRAFT_MAX_AGE_MS) {
        window.localStorage.removeItem(editDraftKey);
        return;
      }
      setEditDraftPrompt(draft);
    } catch {
      try { window.localStorage.removeItem(editDraftKey); } catch (_) {}
    }
  };

  // T23: debounced autosave for edit mode.
  useEffect(() => {
    if (!editing) return;
    if (editDraftPrompt) return;
    const hasContent = (editTitle && editTitle.trim()) || (editContent && editContent.trim());
    if (!hasContent) return;
    setAutosaveStatus('saving');
    const timer = setTimeout(() => {
      try {
        const payload = {
          title: editTitle,
          content: editContent,
          category: editCategory,
          tags: editTags,
          entryDate: editDate,
          savedAt: Date.now(),
        };
        window.localStorage.setItem(editDraftKey, JSON.stringify(payload));
        setAutosaveSavedAt(payload.savedAt);
        setAutosaveStatus('saved');
      } catch {
        setAutosaveStatus('failed');
      }
    }, AUTOSAVE_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [editing, editTitle, editContent, editCategory, editTags, editDate, editDraftPrompt, editDraftKey]);

  const handleRestoreEditDraft = () => {
    if (!editDraftPrompt) return;
    setEditTitle(editDraftPrompt.title || '');
    setEditContent(editDraftPrompt.content || '');
    if (editDraftPrompt.category) setEditCategory(editDraftPrompt.category);
    if (Array.isArray(editDraftPrompt.tags)) setEditTags(editDraftPrompt.tags);
    if (editDraftPrompt.entryDate) setEditDate(editDraftPrompt.entryDate);
    setAutosaveSavedAt(editDraftPrompt.savedAt || null);
    setAutosaveStatus(editDraftPrompt.savedAt ? 'saved' : 'idle');
    setEditDraftPrompt(null);
  };

  const handleDiscardEditDraft = () => {
    try { window.localStorage.removeItem(editDraftKey); } catch (_) {}
    setEditDraftPrompt(null);
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      const res = await apiClient.put(`/api/entries/${id}`, {
        title: editTitle,
        content: editContent,
        category: editCategory,
        tags: editTags,
        entryDate: editDate,
      });
      const updated = res.data.data?.entry || res.data.data;
      setEntry(updated || { ...entry, title: editTitle, content: editContent, category: editCategory, tags: editTags, entryDate: editDate });
      setEditing(false);
      // T23: edit persisted — drop the local draft.
      try { window.localStorage.removeItem(editDraftKey); } catch (_) {}
      setAutosaveStatus('idle');
      setAutosaveSavedAt(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save changes.');
    } finally {
      setSaving(false);
    }
  };

  // Two-step delete: button click opens ConfirmDialog; confirm performs
  // the actual deletion. Replaces window.confirm() for consistent UX.
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const handleConfirmDelete = async () => {
    setShowDeleteConfirm(false);
    try {
      await apiClient.delete(`/api/entries/${id}`);
      navigate('/journal');
    } catch {
      setError('Failed to delete entry.');
    }
  };

  const suggestTags = async () => {
    if (!editContent.trim()) return;
    setTagLoading(true);
    setTagError('');
    try {
      const res = await apiClient.post('/api/ai/auto-tag', { content: editContent, title: editTitle, category: editCategory });
      const suggested = (res.data && res.data.data && res.data.data.tags) ? res.data.data.tags : [];
      setEditTags(prev => {
        const merged = [...prev];
        for (const t of suggested) {
          if (!merged.includes(t)) merged.push(t);
        }
        return merged;
      });
    } catch {
      setTagError('Could not suggest tags. You can add them manually.');
    } finally {
      setTagLoading(false);
    }
  };

  const addEditTag = (val) => {
    const trimmed = val.trim().toLowerCase().replace(/^#/, '');
    if (!trimmed) { setEditTagInput(''); return; }
    if (editTags.length >= TAGS_MAX) {
      setTagError(`Maximum ${TAGS_MAX} tags allowed`);
      setEditTagInput('');
      return;
    }
    if (trimmed.length > TAG_LENGTH_MAX) {
      setTagError(`Tags must be ${TAG_LENGTH_MAX} characters or fewer`);
      setEditTagInput('');
      return;
    }
    if (!editTags.includes(trimmed)) {
      setEditTags([...editTags, trimmed]);
      setTagError('');
    }
    setEditTagInput('');
  };

  const handleEditTagKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addEditTag(editTagInput);
    }
  };

  if (loading) return (
    <div style={styles.page}>
      <NavBar />
      <div style={styles.pageContent}><p style={styles.loadingText}>Loading entry...</p></div>
    </div>
  );

  if (!entry && !loading) return (
    <div style={styles.page}>
      <NavBar />
      <div id="main-content" tabIndex={-1} style={styles.pageContent}>
        <Link to="/journal" style={styles.backLink}>← Back to Journal</Link>
        <div role="alert" style={{ backgroundColor: '#fef2f2', border: '1px solid #fecaca', color: '#ef4444', borderRadius: '10px', padding: '2rem', textAlign: 'center' }}>
          <p style={{ marginBottom: '0.75rem' }}>{error || 'Entry not found.'}</p>
          <button onClick={fetchEntry} style={{ ...styles.btn, ...styles.primaryBtn, padding: '0.5rem 1rem' }}>Retry</button>
        </div>
      </div>
    </div>
  );

  const tags = Array.isArray(entry?.tags)
    ? entry.tags
    : (typeof entry?.tags === 'string' ? JSON.parse(entry?.tags || '[]') : []);
  const color = CATEGORY_COLORS[entry?.category] || '#94a3b8';

  return (
    <div style={styles.page}>
      <NavBar />
      {/* T27: keyframes for the recording-dot pulse. Inlined because this */}
      {/* codebase doesn't use a global CSS file.                            */}
      <style>{`@keyframes bragsheetVoicePulse { 0%,100% { opacity: 1; transform: scale(1); } 50% { opacity: 0.4; transform: scale(0.85); } }`}</style>
      <div id="main-content" tabIndex={-1} style={styles.pageContent}>
        <Link to="/journal" style={styles.backLink}>← Back to Journal</Link>

        <div style={styles.card}>
          {error && <div style={styles.error} role="alert">{error}</div>}

          {editing ? (
            <>
              {editDraftPrompt && (
                <div
                  role="status"
                  style={{
                    backgroundColor: '#f0fdfa',
                    border: '1px solid #99d5cf',
                    color: '#01696F',
                    borderRadius: '8px',
                    padding: '0.65rem 0.875rem',
                    marginBottom: '1rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    flexWrap: 'wrap',
                    fontSize: '0.875rem',
                  }}
                >
                  <span>
                    Resume your draft from {formatDraftTimestamp(editDraftPrompt.savedAt)}?
                  </span>
                  <button
                    type="button"
                    onClick={handleRestoreEditDraft}
                    style={{
                      padding: '0.3rem 0.75rem',
                      borderRadius: '6px',
                      border: 'none',
                      backgroundColor: '#01696F',
                      color: '#ffffff',
                      fontSize: '0.8rem',
                      fontWeight: '600',
                      cursor: 'pointer',
                    }}
                  >
                    Restore
                  </button>
                  <button
                    type="button"
                    onClick={handleDiscardEditDraft}
                    style={{
                      padding: '0.3rem 0.75rem',
                      borderRadius: '6px',
                      border: '1px solid #99d5cf',
                      backgroundColor: '#ffffff',
                      color: '#01696F',
                      fontSize: '0.8rem',
                      fontWeight: '600',
                      cursor: 'pointer',
                    }}
                  >
                    Discard
                  </button>
                </div>
              )}
              <div style={styles.formGroup}>
                <label style={styles.label}>
                  <span>Title</span>
                  <span style={{ fontSize: '0.8rem', color: editTitle.length > TITLE_MAX - 20 ? '#ef4444' : '#94a3b8' }}>{editTitle.length}/{TITLE_MAX}</span>
                </label>
                <input
                  style={styles.input}
                  value={editTitle}
                  onChange={e => setEditTitle(e.target.value)}
                  maxLength={TITLE_MAX}
                  onFocus={(e) => { e.target.style.borderColor = '#01696F'; e.target.style.boxShadow = '0 0 0 3px rgba(1,105,111,0.15)'; }}
                  onBlur={(e) => { e.target.style.borderColor = '#e2e8f0'; e.target.style.boxShadow = 'none'; }}
                />
                <div
                  aria-live="polite"
                  style={{
                    fontSize: '0.8rem',
                    color: autosaveStatus === 'failed' ? '#ef4444' : '#94a3b8',
                    marginTop: '0.3rem',
                    minHeight: '1.1em',
                  }}
                >
                  {autosaveStatus === 'saving' && 'Saving…'}
                  {autosaveStatus === 'saved' && autosaveSavedAt && `Saved at ${formatSavedTime(autosaveSavedAt)}`}
                  {autosaveStatus === 'failed' && 'Save failed'}
                </div>
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>
                  <span>Content</span>
                  <span style={{ fontSize: '0.8rem', color: editContent.length > CONTENT_MAX - 5000 ? '#ef4444' : '#94a3b8' }}>{editContent.length.toLocaleString()}/{CONTENT_MAX.toLocaleString()}</span>
                </label>
                <textarea
                  style={{ ...styles.textarea, minHeight: isMobile ? '140px' : '200px' }}
                  value={editContent}
                  onChange={e => setEditContent(e.target.value)}
                  maxLength={CONTENT_MAX}
                  onFocus={(e) => { e.target.style.borderColor = '#01696F'; e.target.style.boxShadow = '0 0 0 3px rgba(1,105,111,0.15)'; }}
                  onBlur={(e) => { e.target.style.borderColor = '#e2e8f0'; e.target.style.boxShadow = 'none'; }}
                />
                {/* T27: prominent voice input row directly under the textarea. */}
                {isSupported && (
                  <div style={styles.voiceRow}>
                    <button
                      type="button"
                      onClick={handleToggleRecording}
                      onMouseEnter={(e) => {
                        const hover = isListening ? styles.voiceBtnActiveHover : styles.voiceBtnHover;
                        e.currentTarget.style.backgroundColor = hover.backgroundColor;
                        if (hover.borderColor) e.currentTarget.style.borderColor = hover.borderColor;
                      }}
                      onMouseLeave={(e) => {
                        const base = isListening ? styles.voiceBtnActive : styles.voiceBtn;
                        e.currentTarget.style.backgroundColor = base.backgroundColor;
                        e.currentTarget.style.borderColor = base.borderColor;
                      }}
                      style={{
                        ...styles.voiceBtn,
                        ...(isListening ? styles.voiceBtnActive : {}),
                      }}
                      aria-label={isListening ? 'Stop voice input' : 'Start voice input'}
                      aria-pressed={isListening}
                    >
                      {isListening ? (
                        <span style={styles.recordingDot} aria-hidden="true" />
                      ) : (
                        <svg
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          aria-hidden="true"
                        >
                          <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
                          <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                          <line x1="12" y1="19" x2="12" y2="23" />
                          <line x1="8" y1="23" x2="16" y2="23" />
                        </svg>
                      )}
                      <span>{isListening ? 'Recording\u2026 tap to stop' : 'Voice input'}</span>
                    </button>
                  </div>
                )}
                {!isSupported && (
                  <div style={styles.micNotSupported}>
                    Voice input is not supported in this browser. Try Chrome or Edge.
                  </div>
                )}
                {speechError && <div style={styles.speechError}>{speechError}</div>}
                {isListening && transcript && (
                  <div style={styles.interimText}>
                    Hearing: "{transcript}"
                  </div>
                )}
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}><span>Category</span></label>
                <select style={styles.select} value={editCategory} onChange={e => setEditCategory(e.target.value)}>
                  {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>
                  <span>Tags</span>
                  <span style={{ fontSize: '0.8rem', color: editTags.length >= TAGS_MAX ? '#ef4444' : '#94a3b8' }}>{editTags.length}/{TAGS_MAX}</span>
                </label>
                <input
                  style={styles.input}
                  value={editTagInput}
                  onChange={e => setEditTagInput(e.target.value)}
                  onKeyDown={handleEditTagKeyDown}
                  onBlur={(e) => { if (editTagInput.trim()) addEditTag(editTagInput); e.target.style.borderColor = '#e2e8f0'; e.target.style.boxShadow = 'none'; }}
                  onFocus={(e) => { e.target.style.borderColor = '#01696F'; e.target.style.boxShadow = '0 0 0 3px rgba(1,105,111,0.15)'; }}
                  disabled={editTags.length >= TAGS_MAX}
                  placeholder={editTags.length >= TAGS_MAX ? 'Maximum tags reached' : 'Add tag, press Enter'}
                />
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '0.3rem' }}>
                  <span style={styles.tagHint}>Press Enter or comma to add</span>
                  <button
                    type="button"
                    onClick={suggestTags}
                    disabled={tagLoading || !editContent.trim()}
                    style={{
                      padding: '0.3rem 0.8rem',
                      borderRadius: '8px',
                      fontSize: '0.8rem',
                      backgroundColor: tagLoading ? '#94a3b8' : '#01696F',
                      color: '#ffffff',
                      border: 'none',
                      cursor: tagLoading || !editContent.trim() ? 'not-allowed' : 'pointer',
                      opacity: tagLoading || !editContent.trim() ? 0.6 : 1,
                    }}
                  >
                    {tagLoading ? 'Suggesting...' : 'Suggest Tags'}
                  </button>
                </div>
                {tagError && (
                  <div style={{ color: '#ef4444', fontSize: '0.8rem', marginTop: '0.25rem' }}>
                    {tagError}
                  </div>
                )}
                {editTags.length > 0 && (
                  <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', marginTop: '0.5rem' }}>
                    {editTags.map(tag => (
                      <span key={tag} style={styles.chip}>
                        #{tag}
                        <button type="button" style={styles.chipRemove} onClick={() => setEditTags(editTags.filter(t => t !== tag))}>
                          &times;
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}><span>Entry Date</span></label>
                <input
                  style={styles.input}
                  type="date"
                  value={editDate}
                  onChange={e => setEditDate(e.target.value)}
                  onFocus={(e) => { e.target.style.borderColor = '#01696F'; e.target.style.boxShadow = '0 0 0 3px rgba(1,105,111,0.15)'; }}
                  onBlur={(e) => { e.target.style.borderColor = '#e2e8f0'; e.target.style.boxShadow = 'none'; }}
                />
              </div>
              <div style={styles.btnRow}>
                <button style={{ ...styles.btn, ...styles.outlineBtn }} onClick={() => setEditing(false)}>Cancel</button>
                <button
                  style={{ ...styles.btn, ...styles.primaryBtn, ...(saving ? styles.btnDisabled : {}) }}
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </>
          ) : (
            <>
              <div style={styles.titleRow}>
                <h1 style={styles.entryTitle}>{entry.title}</h1>
              </div>
              <div style={styles.meta}>
                <span style={styles.badge(color)}>{entry.category}</span>
                <span style={styles.date}>{formatDate(entry.entryDate || entry.createdAt)}</span>
              </div>
              {tags.length > 0 && (
                <div style={styles.tags}>
                  {tags.map((t, i) => <span key={i} style={styles.tag}>#{t}</span>)}
                </div>
              )}
              <p style={styles.entryText}>{entry.content}</p>
              <div style={styles.btnRow}>
                <button style={{ ...styles.btn, ...styles.primaryBtn }} onClick={startEdit}>Edit</button>
                <button style={{ ...styles.btn, ...styles.dangerBtn }} onClick={() => setShowDeleteConfirm(true)}>Delete</button>
              </div>
            </>
          )}
        </div>
      </div>
      <ConfirmDialog
        open={showDeleteConfirm}
        title="Delete this entry?"
        message="This entry will be permanently removed from your journal. This can't be undone."
        confirmText="Delete entry"
        onConfirm={handleConfirmDelete}
        onCancel={() => setShowDeleteConfirm(false)}
      />
    </div>
  );
}
